import { Building02, Edit01, LinkBroken01 } from '@untitled-ui/icons-react';
import { type ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

import { IntegrationStatus } from '../entity/api/organization-integration';
import { tGlobal, tNamespace } from '../services/translation';
import { Badge } from './badge';
import { Button } from './buttons/button';
import { IconButton } from './buttons/icon-button';
import {
  AppDropdownBody,
  type DropdownItemProps,
  DropdownMenu,
  DropdownMenuItem,
} from './dropdown-menu';
import { AnimatedDotsIcons } from './icons/animatedDotsIcons';

const t = tNamespace('app.integration-card');

type IntegrationAccount = {
  id: string;
  name: string;
  url: string;
  status: IntegrationStatus;
};

type IntegrationCardProps = {
  icon: ReactNode;
  title: string;
  description: string;
  accounts?: IntegrationAccount[];
  onConnect?: () => void;
  onDisconnect?: (accountId: string) => void;
  onEdit?: (accountId: string) => void;
  className?: string;
  isComingSoon?: boolean;
};

export function IntegrationCard(props: IntegrationCardProps) {
  const {
    icon,
    title,
    description,
    accounts = [],
    onConnect,
    onDisconnect,
    onEdit,
    className,
    isComingSoon,
  } = props;
  const isConnected = accounts.length > 0;

  return (
    <div
      className={twMerge(
        'rounded-md border border-neutral-100 px-6 py-8',
        isConnected ? 'bg-neutral-50' : 'bg-white',
        className
      )}
    >
      {/* Header Section */}
      <div className="flex items-center gap-6">
        <div
          className={twMerge(
            'flex size-[60px] shrink-0 items-center justify-center rounded-full bg-neutral-50',
            isConnected ? 'bg-neutral-100' : 'bg-neutral-50'
          )}
        >
          {icon}
        </div>
        <div className="flex items-start gap-4">
          <div className="flex flex-col gap-1">
            <p className="font-body-1-sb text-black">{title}</p>
            <p className="font-body-2-r text-dark-gray">{description}</p>
          </div>
        </div>
        <div className="ml-auto shrink-0">
          {isComingSoon ? (
            <Badge color="gray" size="lg">
              {tGlobal('comingSoon')}
            </Badge>
          ) : null}
          {!isComingSoon && isConnected ? (
            <Button variant="outline" size="md" onClick={onConnect}>
              {t('connectAnother')}
            </Button>
          ) : null}
          {!isComingSoon && !isConnected ? (
            <Button variant="accent" size="md" onClick={onConnect}>
              {t('connect')}
            </Button>
          ) : null}
        </div>
      </div>
      {isConnected ? <div className="my-6 h-[1px] w-full bg-neutral-200" /> : null}

      {/* Connected Accounts Section */}
      {isConnected && accounts.length > 0 ? (
        <div className="flex flex-col gap-6">
          {accounts.map(function (account) {
            return (
              // eslint-disable-next-line no-use-before-define
              <IntegrationAccountItem
                key={account.id}
                account={account}
                onDisconnect={onDisconnect}
                onEdit={onEdit}
              />
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

type IntegrationAccountItemProps = {
  account: IntegrationAccount;
  onDisconnect?: (accountId: string) => void;
  onEdit?: (accountId: string) => void;
};

function IntegrationAccountItem(props: IntegrationAccountItemProps) {
  const { account, onDisconnect, onEdit } = props;

  const dropdownItems: DropdownItemProps[] = [
    {
      label: (
        <div className="font-body-2-m flex items-center justify-start gap-2">
          <Edit01 width={16} height={16} />
          <span>{t('editIntegrationName')}</span>
        </div>
      ),
      onClick: function () {
        if (onEdit != null) {
          onEdit(account.id);
        }
      },
    },
    {
      label: (
        <div className="font-body-2-m flex items-center justify-start gap-2 text-red-500">
          <LinkBroken01 width={16} height={16} className="text-red-500" />
          <span>{t('disconnectIntegration')}</span>
        </div>
      ),
      onClick: function () {
        if (onDisconnect != null) {
          onDisconnect(account.id);
        }
      },
    },
  ];

  return (
    <div className="border-branding flex items-center justify-between gap-4 rounded-md border border-neutral-100 bg-white p-6">
      <div className="flex items-center gap-3">
        <div className="flex size-[46px] items-center justify-center rounded-full bg-neutral-100">
          <Building02 width={24} height={24} className="text-branding-accent" />
        </div>
        <div className="flex flex-col">
          <span className="font-body-2-sb text-black">{account.name}</span>
          <span className="font-body-3-r text-dark-gray">{account.url}</span>
        </div>
      </div>
      <div className="flex items-center gap-3">
        {account.status == IntegrationStatus.Active ? (
          <Badge size="sm" color="green">
            {t('connected')}
          </Badge>
        ) : null}
        {account.status == IntegrationStatus.Failed ? (
          <Badge size="sm" color="red">
            {t('failed')}
          </Badge>
        ) : null}
        {account.status == IntegrationStatus.Pending ? (
          <Badge size="sm" color="neutral">
            {t('pending')}
          </Badge>
        ) : null}
        {account.status == IntegrationStatus.Revoked ? (
          <Badge size="sm" color="yellow">
            {t('revoked')}
          </Badge>
        ) : null}
        <DropdownMenu
          trigger={
            <IconButton
              icon={<AnimatedDotsIcons groupName="group/dots-icon" />}
              variant="ghost-neutral"
              size="xs"
              isDiv
            />
          }
          triggerClassName="group/dots-icon"
          closeOnSelect
        >
          <AppDropdownBody>
            {dropdownItems.map(function (item, index) {
              return <DropdownMenuItem key={index} {...item} />;
            })}
          </AppDropdownBody>
        </DropdownMenu>
      </div>
    </div>
  );
}
